

















































































































import Vue from 'vue'
import Component from 'vue-class-component'
import { Prop } from 'vue-property-decorator'
import PaymentCardComponent from '@/components/PaymentCardComponent.vue'
import * as api from '@/services/ApiService'
import appManager from '@/services/AppManager'
import { RoutePaths } from '@/router'
import Spinner from '@/components/Spinner.vue'
import subscriptionValues, { TierInfo } from '@/model/SubscriptionValues'
import SubscriptionPricingTable from '@/components/subscriptions/SubscriptionPricingTable.vue'
import { Coupon } from '@/core/ApiTypes'
import FormTextField from '@/components/FormTextField.vue'
import { isNotBlank } from '@/core/Utils'
import { isCouponAvailable } from '@/model/Helpers'
import { HttpStatus } from '@/core/Http'
import { extractResponseStatus } from '@/services/ApiService'

@Component({
    components: {
        FormTextField,
        Spinner,
        SubscriptionPricingTable,
        PaymentCardComponent
    }
})
export default class AddSubscriptionPanel extends Vue {
    @Prop({ required: true })
    companyId!: string
    @Prop({ required: true })
    stripeCustomerId!: string
    monthlyPriceId = subscriptionValues.monthlyPriceId
    yearlyPriceId = subscriptionValues.yearlyPriceId
    monthlyTiers: TierInfo[] = subscriptionValues.monthlyTiers
    yearlyTiers: TierInfo[] = subscriptionValues.yearlyTiers
    monthlyOrYearlyIndex: number = 1
    quantityText: string = '1'
    stripeCouponId: string|null = null
    coupon: Coupon|null = null
    isLoadingCoupon: boolean= false
    isLoadingTaxRatePercentage = false
    taxRatePercentage: number = 0
    isConfirmDialogVisible = false
    isPaying: boolean = false
    paymentMethodId: string|undefined

    getPriceText(price: number): string {
        return subscriptionValues.getPriceText(price)
    }

    get quantity(): number {
        return +this.quantityText
    }

    get isQuantityValid(): boolean {
        const quantity = this.quantity
        return Number.isFinite(quantity) && quantity > 0
    }

    getRecurringTitle(): string {
        return (this.monthlyOrYearlyIndex === 0 ? 'Monthly' : 'Yearly') + ' Total'
    }

    getCouponTitle(): string {
        return 'First ' + (this.monthlyOrYearlyIndex === 0 ? 'Month' : 'Year') + ' Discount'
    }

    getRecurringPrice(): number {
        let total = 0;
        if (this.isQuantityValid) {
            const tiers = (this.monthlyOrYearlyIndex === 1) ? this.yearlyTiers : this.monthlyTiers
            total = subscriptionValues.getSubtotalPrice(tiers, this.quantity)
        }
        return total
    }

    getRecurringPriceText(): string {
        const price = this.getRecurringPrice()
        return this.getPriceText(price)
    }

    getCouponPrice(): number {
        const amountOff = this.coupon?.data?.amountOff || 0
        return (amountOff < 0) ? 0 : amountOff
    }

    getCouponPriceText(): string {
        const price = this.getCouponPrice()
        return this.getPriceText(price)
    }

    getSubtotalPrice(): number {
        const total = this.getRecurringPrice() - this.getCouponPrice();
        return (total < 0) ? 0 : total
    }

    getSubtotalPriceText(): string {
        const price = this.getSubtotalPrice()
        return this.getPriceText(price)
    }

    getTaxesPrice(): number {
        return (this.taxRatePercentage / 100.0) * this.getSubtotalPrice()
    }

    getTaxesText(): string {
        const price = this.getTaxesPrice()
        return this.getPriceText(price)
    }

    getTotalPrice(): number {
        return this.getSubtotalPrice() + this.getTaxesPrice()
    }

    getTotalPriceText(): string {
        const price = this.getTotalPrice()
        return this.getPriceText(price)
    }

    async onApplyCouponPressed() {
        this.coupon = null
        this.isLoadingCoupon = true


        if (this.stripeCouponId && isNotBlank(this.stripeCouponId)) {
            try {
                const coupon = await api.getCoupon(this.stripeCouponId)
                if (isCouponAvailable(coupon)) {
                    this.coupon = coupon
                } else {
                    appManager.alertManager.publishError('Coupon is no longer available')
                }
            } catch (error) {
                if (extractResponseStatus(error) === HttpStatus.NOT_FOUND) {
                    appManager.alertManager.publishError('Invalid coupon.')
                } else {
                    appManager.alertManager.publishError('Error loading coupon. Please try again later.')
                    console.error(`Error loading coupon: ${JSON.stringify(error)}`)
                }
            }
        }

        this.isLoadingCoupon = false
    }

    async onPayRequested(paymentMethodId: string, zipCode: string|undefined): Promise<void> {
        this.paymentMethodId = paymentMethodId
        this.isConfirmDialogVisible = true

        // load the tax rates for this zip code
        if (zipCode) {
            this.isLoadingTaxRatePercentage = true
            try {
                const response = await api.getTaxRatesByZipCode(zipCode)
                let totalPercentage = 0
                for (const taxRate of response.taxRates) {
                    if (!taxRate.data.inclusive)
                        totalPercentage += taxRate.data.percentage
                }
                this.taxRatePercentage = totalPercentage
            } catch (error) {
                appManager.alertManager.publishError('Error loading tax rates. Please try again later.')
                console.error(`Error loading tax rates: ${error}`)
            }
        } else {
            this.taxRatePercentage = 0
        }
        this.isLoadingTaxRatePercentage = false
    }

    async onPayConfirmed() {
        this.isPaying = true
        if (this.companyId && this.stripeCustomerId && this.isQuantityValid && this.paymentMethodId) {
            try {
                await appManager.subscriptionManager.createSubscription({
                    companyId: this.companyId,
                    stripeCustomerId: this.stripeCustomerId,
                    stripePriceId: (this.monthlyOrYearlyIndex === 1) ? this.yearlyPriceId : this.monthlyPriceId,
                    quantity: this.quantity,
                    stripePaymentMethodId: this.paymentMethodId,
                    stripeCouponId: this.coupon?.id || undefined,
                })

                appManager.alertManager.publishSuccess('Subscription successfully added')

                // if we succeed, go back to the main subscriptions page
                await this.$router.replace(RoutePaths.SUBSCRIPTIONS)
            } catch (error) {
                console.error(error)
                console.error(JSON.stringify(error))
            }
        }
        this.isPaying = false
        this.isConfirmDialogVisible = false
    }
}
